/**
 * BulkActionsContext
 *
 * Allows any page to "hoist" the bulk actions bar into the app-layout root,
 * which sits completely outside all overflow / scroll containers so that
 * `position: fixed` works reliably in every environment (including iframes).
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { BulkAction } from '@/components/bulk-actions-bar';

interface BulkActionsState {
  selectedCount: number;
  onAction: (action: BulkAction) => void;
  onClear: () => void;
}

interface BulkActionsContextValue {
  bulkActionsState: BulkActionsState | null;
  registerBulkActions: (state: BulkActionsState | null) => void;
}

const BulkActionsContext = createContext<BulkActionsContextValue>({
  bulkActionsState: null,
  registerBulkActions: () => {},
});

export function BulkActionsProvider({ children }: { children: ReactNode }) {
  const [bulkActionsState, setBulkActionsState] = useState<BulkActionsState | null>(null);

  const registerBulkActions = useCallback((state: BulkActionsState | null) => {
    setBulkActionsState(state);
  }, []);

  return (
    <BulkActionsContext.Provider value={{ bulkActionsState, registerBulkActions }}>
      {children}
    </BulkActionsContext.Provider>
  );
}

export function useBulkActions() {
  return useContext(BulkActionsContext);
}
